import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, shareReplay, switchMap, throwError } from 'rxjs';
import { AuthService, RefreshTokenResponse } from './auth.service';

let isRedirectingToLogin = false;

// Shared across concurrent requests: if several requests 401 at the same
// moment (e.g. a page that fires 3 parallel calls right as the access
// token expires), they must all wait on the SAME refresh call - not each
// trigger their own - or the backend would rotate the refresh cookie out
// from under itself (issue #160: every refresh call revokes the token it
// used and issues a new one). shareReplay(1) makes every subscriber
// (concurrent or not) share one underlying HTTP call and see the same
// success/error; finalize() clears it once that call settles so the
// *next* refresh cycle (a later, separate 401) starts a fresh one.
let refreshInFlight: Observable<RefreshTokenResponse> | null = null;

function getOrStartRefresh(authService: AuthService): Observable<RefreshTokenResponse> {
  if (!refreshInFlight) {
    refreshInFlight = authService.refreshToken().pipe(
      finalize(() => {
        refreshInFlight = null;
      }),
      shareReplay(1)
    );
  }
  return refreshInFlight;
}

function forceLogoutAndRedirect(authService: AuthService, router: Router): void {
  // clearLocalSession(), not logout(): we only get here because the refresh
  // call itself was rejected, so the refresh cookie is already dead - POSTing
  // /auth/logout to revoke it would be a pointless extra request on a path
  // that is already failing, and it re-enters the HTTP stack while we are
  // still inside an interceptor error handler.
  authService.clearLocalSession();

  const currentUrl = router.url;
  const alreadyOnLogin = currentUrl.startsWith('/login');
  if (!alreadyOnLogin && !isRedirectingToLogin) {
    isRedirectingToLogin = true;
    const returnUrl = encodeURIComponent(currentUrl || '/');
    window.location.assign(`/login?returnUrl=${returnUrl}`);
  }
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const token = authService.getToken();

  // withCredentials on every request - required for the backend's httpOnly
  // refresh cookie (issue #160) to be sent at all. Without it the cookie
  // set on login never comes back on later requests (cross-port in local
  // dev, cross-origin in staging/prod - cookies aren't attached to
  // cross-origin requests by default), and /api/auth/refresh always fails.
  const authReq = req.clone({
    withCredentials: true,
    setHeaders: token ? { Authorization: `Bearer ${token}` } : {},
  });

  return next(authReq).pipe(
    catchError((err: unknown) => {
      // 401 means the session itself is invalid (missing/expired/bad
      // token) - that's the only case that should force a logout. 403
      // means the session is fine but this specific action isn't
      // permitted (missing plan capability, wrong owner, admin-only
      // route, etc.) - every one of those is a normal, expected response
      // for a regular user and must never nuke their session. Confirmed
      // live: a plan-limited user's own "Show Keywords" click (blocked by
      // a capability check, a legitimate 403) was logging them out and
      // redirecting to /login - the exact bug this guards against.
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }

      // A 401 on the refresh/logout calls themselves means the refresh
      // cookie is already gone (expired/revoked/never existed) - there is
      // nothing left to refresh with. Let it fail as-is: refreshToken()'s
      // own caller (below) already handles that as a hard logout, and
      // logout()'s own call ignores its response either way - retrying
      // either through *another* refresh would just loop.
      if (req.url.includes('/auth/refresh') || req.url.includes('/auth/logout')) {
        return throwError(() => err);
      }

      // No session was ever established in this browser: a 401 here is just
      // "you are not logged in", the correct answer for an anonymous visitor,
      // not an expired session to recover. Several call sites request
      // /users/me without checking for a token first (ProfileCompletionGuard
      // via UserService.getMyProfile(), for one), so treating every such 401
      // as a dead session sent a logged-out visitor into refresh -> fail ->
      // logout -> hard redirect to /login -> page reload -> repeat, an
      // endless reload loop that never let them reach the Google button.
      if (!authService.hasStoredSession()) {
        return throwError(() => err);
      }

      return getOrStartRefresh(authService).pipe(
        switchMap((res) =>
          next(
            req.clone({
              withCredentials: true,
              setHeaders: { Authorization: `Bearer ${res.token}` },
            })
          )
        ),
        catchError((refreshErr: unknown) => {
          // Refresh itself failed (cookie expired/revoked) - now it's a
          // real logout, same as the old immediate-logout-on-401 behavior.
          forceLogoutAndRedirect(authService, router);
          return throwError(() => refreshErr);
        })
      );
    })
  );
};
